/**
 * NotificationService — خدمة الإشعارات
 * يدير: Web Push permission + native OS notifications + polling
 */

const API = process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1';
const TK  = 'watrly_customer_token';

export function getToken() {
  return localStorage.getItem(TK);
}

function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : {};
}

/* ── Register Service Worker ── */
async function registerSW() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    return reg;
  } catch (e) { return null; }
}

/* ── urlBase64ToUint8Array helper for VAPID key ── */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw     = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

/* ── Request OS notification permission + subscribe push ── */
export async function requestPermission() {
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';

  const result = Notification.permission === 'granted'
    ? 'granted'
    : await Notification.requestPermission();

  if (result === 'granted') {
    await subscribePush();
  }
  return result;
}

/* ── Subscribe to Web Push ── */
export async function subscribePush() {
  if (!getToken()) return;
  try {
    const sw = await registerSW();
    if (!sw) return;

    // Get VAPID public key from server
    const keyRes = await fetch(`${API}/customer/notifications/vapid-public-key`);
    if (!keyRes.ok) return;
    const { publicKey } = await keyRes.json();

    const subscription = await sw.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });

    const subJson = subscription.toJSON();
    await fetch(`${API}/customer/notifications/subscribe`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint: subJson.endpoint,
        keys:     subJson.keys,
        userAgent: navigator.userAgent,
      }),
    });
  } catch (e) {
    console.warn('[Push] subscribe failed:', e);
  }
}

/* ── Show OS native notification ── */
export function showNativeNotification(title, body, options = {}) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const n = new Notification(title, {
    body,
    icon:  '/assets/images/logo/blue-logo.png',
    badge: '/assets/images/logo/blue-logo.png',
    tag:   options.tag || 'watrly-notif',
    renotify: true,
    silent: false,
    ...options,
  });
  n.onclick = () => {
    window.focus();
    if (options.onClick) options.onClick();
    n.close();
  };
  return n;
}

/* ── Fetch unread count ── */
export async function fetchUnreadCount() {
  try {
    const res = await fetch(`${API}/customer/notifications/unread-count`, { headers: authHeaders() });
    if (!res.ok) return 0;
    const d = await res.json();
    return d.count || 0;
  } catch { return 0; }
}

/* ── Fetch notifications list ── */
export async function fetchNotifications(unreadOnly = false) {
  try {
    const url = `${API}/customer/notifications?limit=30${unreadOnly ? '&unread_only=true' : ''}`;
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) return [];
    const d = await res.json();
    return d.notifications || [];
  } catch { return []; }
}

/* ── Mark notifications as read ── */
export async function markRead(ids = null) {
  try {
    await fetch(`${API}/customer/notifications/mark-read`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(ids ? { ids } : {}),
    });
  } catch {}
}

/* ─────────────────────────────────────────────
   WebSocket — real-time instant notifications
   + light polling fallback every 30s
   ─────────────────────────────────────────────*/
const WS_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1')
  .replace(/^http/, 'ws')
  .replace('/api/v1', '');

let _lastSeenIds = new Set();
let _wsInstance   = null;
let _wsRetryTimer = null;
let _wsRetries    = 0;

/* Seed existing IDs so they never trigger as "new" */
export function seedSeenIds(ids = []) {
  ids.forEach(id => _lastSeenIds.add(id));
}

function _buildWsUrl(customerId) {
  return `${WS_BASE}/api/v1/ws/customer/${customerId}`;
}

export function startRealtimeNotifications(customerId, onNewNotifications) {
  let pollTimer  = null;
  let destroyed  = false;

  /* ── poll helper (used on initial load + fallback) ── */
  const poll = async () => {
    if (!getToken() || destroyed) return;
    const notifs = await fetchNotifications(false);
    const fresh = notifs.filter(n => !n.is_read && !_lastSeenIds.has(n.id));
    if (fresh.length > 0) {
      fresh.forEach(n => _lastSeenIds.add(n.id));
      onNewNotifications(fresh, notifs);
      // OS notification only if page is hidden
      if (document.hidden) {
        fresh.forEach(n => showNativeNotification(n.title, n.body || '', {
          tag: `watrly-poll-${n.id}`, data: n.data,
        }));
      }
    }
  };

  /* ── WebSocket connection ── */
  const connect = () => {
    if (destroyed || !customerId || !getToken()) return;
    const url = _buildWsUrl(customerId);
    const ws  = new WebSocket(url);
    _wsInstance = ws;

    ws.onopen = () => {
      _wsRetries = 0;
      // heartbeat ping every 20s
      const ping = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        else clearInterval(ping);
      }, 20000);
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'notification') {
          const fakeNotif = {
            id: `ws-${Date.now()}`,
            type: msg.notif_type || 'info',
            title: msg.title,
            body:  msg.body,
            data:  msg.data || {},
            is_read: false,
            created_at: new Date().toISOString(),
          };
          _lastSeenIds.add(fakeNotif.id);
          onNewNotifications([fakeNotif], [fakeNotif]);
          // OS notification only if page is hidden/background
          if (document.hidden) {
            showNativeNotification(msg.title, msg.body || '', {
              tag:  `watrly-ws-${Date.now()}`,
              data: msg.data,
            });
          }
        }
      } catch {}
    };

    ws.onclose = () => {
      if (destroyed) return;
      _wsRetries++;
      const delay = Math.min(1000 * 2 ** _wsRetries, 30000);
      _wsRetryTimer = setTimeout(connect, delay);
    };

    ws.onerror = () => ws.close();
  };

  /* ── start ── */
  connect();                                       // WebSocket — instant
  pollTimer = setInterval(poll, 30000);            // light fallback every 30s

  /* ── cleanup ── */
  return () => {
    destroyed = true;
    clearInterval(pollTimer);
    clearTimeout(_wsRetryTimer);
    if (_wsInstance) { _wsInstance.onclose = null; _wsInstance.close(); }
  };
}
